<!-- Conversation Log Page -->
<div class="p-0 md:p-8 min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-indigo-50">

  <div class="max-w-[1600px] mx-auto">
    <!-- Page Header -->
    <div class="px-4 md:px-0 mb-8">
      <div class="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 class="text-3xl md:text-4xl font-bold text-slate-800 mb-2">Conversation History</h1>
          <p class="text-slate-600">Browse and analyze all chat sessions with sentiment tracking</p>
        </div>

        <div class="flex items-center gap-3">
          <button (click)="loadConversations()"
            class="p-3 bg-white text-slate-600 rounded-xl shadow-md border border-slate-200 transition-all hover:scale-105 active:scale-95">
            <svg class="w-6 h-6" [class.animate-spin]="loading" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15">
              </path>
            </svg>
          </button>
          <button (click)="exportConversation()" [disabled]="!selectedConvo"
            class="px-5 py-2.5 bg-white disabled:opacity-50 hover:bg-slate-50 text-slate-700 font-semibold rounded-xl shadow-md transition-all flex items-center gap-2 border border-slate-200">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z">
              </path>
            </svg>
            Export
          </button>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-12 gap-6 h-[calc(100vh-200px)]">

      <!-- Session Sidebar (3/12) -->
      <div
        class="lg:col-span-4 xl:col-span-3 flex flex-col bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
        <div class="bg-gradient-to-r from-purple-500 to-indigo-600 px-6 py-4">
          <h3 class="font-bold text-white flex items-center gap-2">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10">
              </path>
            </svg>
            Recent Sessions
          </h3>
        </div>

        <div class="flex-1 overflow-y-auto p-2 space-y-2">
          <div *ngIf="loading" class="flex flex-col items-center justify-center py-12 text-slate-400">
            <div class="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mb-4"></div>
            <p>Loading sessions...</p>
          </div>

          <button *ngFor="let convo of conversations" (click)="selectConversation(convo)"
            [class]="selectedConvo?.id === convo.id ? 'bg-indigo-50 border-indigo-500 ring-2 ring-indigo-200' : 'bg-slate-50 border-transparent hover:bg-slate-100'"
            class="w-full text-left p-4 rounded-xl border-2 transition-all group">
            <div class="flex items-center justify-between mb-1">
              <span class="text-xs font-bold text-slate-400">{{ (convo.created_at || convo.start_time) | date:'shortTime' }}</span>
              <span class="px-2 py-0.5 bg-slate-200 text-slate-600 rounded text-[10px] font-bold">{{ convo.message_count
                }} msgs</span>
            </div>
            <p class="text-sm font-bold text-slate-800 line-clamp-1 mb-1">
              {{ convo.user_identifier || 'Anonymous User' }}
            </p>
            <p class="text-xs text-slate-500 line-clamp-1 italic">
              "{{ convo.last_message || 'No messages' }}"
            </p>
            <div class="mt-2 flex items-center justify-between">
              <span class="text-[10px] text-slate-400 font-mono">{{ convo.session_id }}</span>
              <span *ngIf="convo.ticket_id"
                class="px-1.5 py-0.5 bg-orange-100 text-orange-600 rounded text-[10px] font-bold">TICKET</span>
            </div>
          </button>

          <div *ngIf="!loading && conversations.length === 0" class="text-center py-12 text-slate-400">
            <p>No chat history available</p>
          </div>
        </div>
      </div>

      <!-- Transcript Area (9/12) -->
      <div
        class="lg:col-span-8 xl:col-span-9 flex flex-col bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">

        <!-- Welcome/Empty State when no convo selected -->
        <div *ngIf="!selectedConvo"
          class="flex-1 flex flex-col items-center justify-center text-slate-400 p-8 text-center">
          <div class="w-24 h-24 bg-slate-50 rounded-full flex items-center justify-center mb-6">
            <svg class="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z">
              </path>
            </svg>
          </div>
          <h2 class="text-2xl font-bold text-slate-700 mb-2">Select a Conversation</h2>
          <p>Choose a session from the list on the left to view the full transcript and sentiment analysis.</p>
        </div>

        <!-- Selected Transcript -->
        <ng-container *ngIf="selectedConvo">
          <!-- Header info -->
          <div class="bg-slate-50 border-b border-slate-200 px-6 py-4 flex items-center justify-between">
            <div class="flex items-center gap-4">
              <div
                class="w-12 h-12 bg-indigo-100 text-indigo-700 rounded-xl flex items-center justify-center font-bold text-xl">
                {{ (selectedConvo.user_identifier || 'A').charAt(0).toUpperCase() }}
              </div>
              <div>
                <h3 class="font-bold text-slate-800">{{ selectedConvo.user_identifier || 'Anonymous User' }}</h3>
                <p class="text-xs text-slate-500">Session ID: {{ selectedConvo.session_id }}</p>
              </div>
            </div>
            <div class="text-right">
              <div class="text-sm font-bold text-slate-700">{{ (selectedConvo.created_at || selectedConvo.start_time) | date:'medium' }}</div>
              <div class="text-xs text-slate-400">Total Duration: {{ calculateDuration() }}</div>
            </div>
          </div>

          <!-- Messages List -->
          <div class="flex-1 overflow-y-auto p-6 space-y-6 bg-slate-50/50">
            <div *ngIf="messagesLoading" class="flex flex-col items-center justify-center py-24">
              <div class="w-10 h-10 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
            </div>

            <div *ngFor="let m of messages; let i = index" class="flex flex-col" [class.items-end]="m.sender === 'user'"
              [class.items-start]="m.sender === 'bot'">

              <div class="max-w-[80%] flex flex-col" [class.items-end]="m.sender === 'user'">
                <!-- Metainfo -->
                <div class="flex items-center gap-2 mb-1 px-1">
                  <span *ngIf="m.sender === 'bot'"
                    class="text-[10px] font-bold text-indigo-600 uppercase tracking-tighter">AI Assistant</span>
                  <span class="text-[10px] text-slate-400">{{ m.timestamp | date:'shortTime' }}</span>
                  <span *ngIf="m.sender === 'user'"
                    class="text-[10px] font-bold text-slate-600 uppercase tracking-tighter">USER</span>
                </div>

                <!-- Message bubble -->
                <div (click)="selectMessage(m.id)"
                  class="p-4 rounded-2xl shadow-sm border cursor-pointer transition-all hover:shadow-md"
                  [ngClass]="m.sender === 'user' ? 'bg-white text-slate-800 border-slate-200 rounded-tr-none' : 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white border-transparent rounded-tl-none'">
                  <p class="text-sm whitespace-pre-line leading-relaxed">{{ m.text }}</p>
                </div>

                <!-- Expanded details -->
                <div *ngIf="selectedMessageId === m.id"
                  class="mt-2 p-3 bg-white border border-slate-200 rounded-xl shadow-sm animate-fadeIn w-full">
                  <div class="flex items-center justify-between mb-2">
                    <div [class]="getSentimentBgColor(m.sentiment)"
                      class="px-3 py-1 rounded-full flex items-center gap-2">
                      <span class="text-lg">{{ getSentimentIcon(m.sentiment) }}</span>
                      <span [class]="getSentimentColor(m.sentiment)" class="text-[10px] font-bold uppercase">{{
                        m.sentiment }}</span>
                    </div>
                    <span class="text-[10px] text-slate-400">Intent: {{ m.keywords?.[0] || 'Unknown' }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Bottom Summary/Ticket -->
          <div class="px-6 py-4 border-t border-slate-200 bg-white flex items-center justify-between">
            <div *ngIf="selectedConvo.ticket_id"
              class="flex items-center gap-2 px-3 py-1.5 bg-orange-100 text-orange-700 rounded-lg border border-orange-200">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z">
                </path>
              </svg>
              <span class="text-xs font-bold">TIED TO TICKET: {{ selectedConvo.ticket_id }}</span>
            </div>
            <div *ngIf="!selectedConvo.ticket_id" class="text-xs text-slate-400">No support ticket associated with this
              session.</div>

            <button *ngIf="selectedConvo.ticket_id"
              class="px-4 py-2 bg-indigo-50 text-indigo-700 rounded-lg text-xs font-bold hover:bg-indigo-100 transition-all">
              View Ticket
            </button>
          </div>
        </ng-container>

      </div>
    </div>
  </div>
</div>